import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { SettingsIcon } from 'lucide-react'
import { Typography } from '@/components/ui/typography'
import { SlippageToleranceTooltip } from '@/components/tooltips/slippage-tolerance'
import { useUserTransactionTTL } from '@/state/user/hooks'
import { useState } from 'react'
import { escapeRegExp } from '@/utils'
import { useUserSingleHopOnly, useUserSlippage } from '@pancakeswap/utils/user'
import { useTranslation } from '@pancakeswap/localization'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { TxDeadlineTooltip } from '@/components/tooltips/tx-deadline'
import { ExpertModeTooltip } from '@/components/tooltips/expert-mode'
import { ExpertModeSwitcher } from '@/components/switchers/expert-mode'
import { useMediaQuery } from '@/hooks/use-media-query'
import { Drawer, DrawerContent, DrawerTrigger } from '@/components/ui/drawer'
import { numberInputPattern, slippageInputPattern } from '@/utils/input'
import { useUserSplitRouteEnable, useUserV2SwapEnable, useUserV3SwapEnable } from '@/state/user/smartRouter'
import { Switch } from '@/components/ui/switch'

enum SlippageError {
  InvalidInput = 'InvalidInput',
  RiskyLow = 'RiskyLow',
  RiskyHigh = 'RiskyHigh',
}

enum DeadlineError {
  InvalidInput = 'InvalidInput',
}

const inputRegex = RegExp(`^\\d*(?:\\\\[.])?\\d*$`) // match escaped "." characters via in a non-capturing group
const THREE_DAYS_IN_SECONDS = 60 * 60 * 24 * 3
const TOLERANCES = [10, 50, 100]

export const SwapSettingsPopover = () => {
  const isDesktop = useMediaQuery('(min-width: 768px)')

  if (isDesktop) {
    return (
      <>
        <Popover>
          <PopoverTrigger>
            <SettingsIcon className={'size-5 cursor-pointer'} />
          </PopoverTrigger>
          <PopoverContent align={'end'} className={'w-96'} onOpenAutoFocus={(e) => e.preventDefault()}>
            <SwapSettingsForm />
          </PopoverContent>
        </Popover>
      </>
    )
  }

  return (
    <>
      <Drawer>
        <DrawerTrigger asChild>
          <SettingsIcon className={'size-5 cursor-pointer'} />
        </DrawerTrigger>
        <DrawerContent>
          <div className="px-6 py-12">
            <SwapSettingsForm />
          </div>
        </DrawerContent>
      </Drawer>
    </>
  )
}

const SwapSettingsForm = () => {
  const [userSlippageTolerance, setUserSlippageTolerance] = useUserSlippage()
  const [ttl, setTtl] = useUserTransactionTTL()
  const [slippageInput, setSlippageInput] = useState('')
  const [deadlineInput, setDeadlineInput] = useState('')
  const [v2Enable, setV2Enable] = useUserV2SwapEnable()
  const [v3Enable, setV3Enable] = useUserV3SwapEnable()
  const [split, setSplit] = useUserSplitRouteEnable()
  const [singleHopOnly, setSingleHopOnly] = useUserSingleHopOnly()

  const { t } = useTranslation()

  const slippageInputIsValid =
    slippageInput === '' || (userSlippageTolerance / 100).toFixed(2) === Number.parseFloat(slippageInput).toFixed(2)
  const deadlineInputIsValid = deadlineInput === '' || (ttl / 60).toString() === deadlineInput

  let slippageError: SlippageError | undefined
  if (slippageInput !== '' && !slippageInputIsValid) {
    slippageError = SlippageError.InvalidInput
  } else if (slippageInputIsValid && userSlippageTolerance < 50) {
    slippageError = SlippageError.RiskyLow
  } else if (slippageInputIsValid && userSlippageTolerance > 500) {
    slippageError = SlippageError.RiskyHigh
  } else {
    slippageError = undefined
  }

  let deadlineError: DeadlineError | undefined
  if (deadlineInput !== '' && !deadlineInputIsValid) {
    deadlineError = DeadlineError.InvalidInput
  } else {
    deadlineError = undefined
  }

  const parseCustomSlippage = (value: string) => {
    if (value === '' || inputRegex.test(escapeRegExp(value))) {
      setSlippageInput(value)

      try {
        const valueAsIntFromRoundedFloat = Number.parseInt((Number.parseFloat(value) * 100).toString())
        if (!Number.isNaN(valueAsIntFromRoundedFloat) && valueAsIntFromRoundedFloat < 5000) {
          setUserSlippageTolerance(valueAsIntFromRoundedFloat)
        }
      } catch (error) {
        console.error(error)
      }
    }
  }

  const parseCustomDeadline = (value: string) => {
    setDeadlineInput(value)

    try {
      const valueAsInt: number = Number.parseInt(value) * 60
      if (!Number.isNaN(valueAsInt) && valueAsInt > 60 && valueAsInt < THREE_DAYS_IN_SECONDS) {
        setTtl(valueAsInt)
      } else {
        deadlineError = DeadlineError.InvalidInput
      }
    } catch (error) {
      console.error(error)
    }
  }

  return (
    <div className={'flex flex-col gap-4'}>
      <div className="flex justify-between">
        <div className={'flex items-center gap-1'}>
          <Typography>{t('Max Slippage')}</Typography>
          <SlippageToleranceTooltip />
        </div>
        <div className={'flex flex-wrap items-center gap-2'}>
          {TOLERANCES.map((tolerance) => (
            <Button
              size={'xs'}
              key={tolerance}
              onClick={() => {
                setSlippageInput('')
                setUserSlippageTolerance(tolerance)
              }}
              variant={userSlippageTolerance === tolerance ? 'primary' : 'secondary'}
              className={'rounded-full!'}
            >
              {(tolerance / 100).toFixed(1)}%
            </Button>
          ))}
          <div className={'flex items-center gap-1'}>
            <div className={'w-[60px] relative'}>
              <Input
                size={'xs'}
                pattern={slippageInputPattern}
                placeholder={(userSlippageTolerance / 100).toFixed(2)}
                value={slippageInput}
                onBlur={() => parseCustomSlippage((userSlippageTolerance / 100).toFixed(2))}
                onChange={(event) => {
                  if (event.currentTarget.validity.valid) parseCustomSlippage(event.target.value.replace(/,/g, '.'))
                }}
                variant={
                  slippageError ? 'warning' : !TOLERANCES.includes(userSlippageTolerance) ? 'primary' : 'default'
                }
                className={'pr-4'}
              />
              <Typography variant={'small'} className={'absolute right-1 top-[7px]'}>
                %
              </Typography>
            </div>
          </div>
        </div>
      </div>
      {!!slippageError && (
        <Typography variant={'tip'} color={slippageError === SlippageError.InvalidInput ? 'destructive' : 'warning'}>
          {slippageError === SlippageError.InvalidInput
            ? t('Enter a valid slippage percentage')
            : slippageError === SlippageError.RiskyLow
            ? t('Your transaction may fail')
            : t('Your transaction may be frontrun')}
        </Typography>
      )}
      <div className={'flex justify-between'}>
        <div className={'flex items-center gap-1'}>
          <Typography>{t('Tx deadline (mins)')}</Typography>
          <TxDeadlineTooltip />
        </div>
        <div>
          <div className={'w-[60px]'}>
            <Input
              size={'xs'}
              pattern={numberInputPattern}
              variant={deadlineError ? 'warning' : 'default'}
              onBlur={() => {
                parseCustomDeadline((ttl / 60).toString())
              }}
              placeholder={(ttl / 60).toString()}
              value={deadlineInput}
              onChange={(event) => {
                if (event.currentTarget.validity.valid) {
                  parseCustomDeadline(event.target.value)
                }
              }}
            />
          </div>
        </div>
      </div>
      <div className="flex justify-between">
        <div className={'flex items-center gap-2'}>
          <Typography>{t('Expert Mode')}</Typography>
          <ExpertModeTooltip />
        </div>
        <ExpertModeSwitcher />
      </div>
      <div className="flex justify-between">
        <Typography>HypieSwap V3</Typography>
        <Switch checked={v3Enable} onCheckedChange={setV3Enable} />
      </div>
      <div className="flex justify-between">
        <Typography>HypieSwap V2</Typography>
        <Switch checked={v2Enable} onCheckedChange={setV2Enable} />
      </div>
    </div>
  )
}
